import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { MapSpot, Live } from "@/common/model/Live";
import { SpotManagementService, NearbySpot, SpotInfo, calculateDistance } from "@/services/SpotManagementService";
import LiveStationPicker from "./LiveStationPicker";
import { SpotAdminLogService, SpotAdminLog, ACTION_TYPES } from "@/services/SpotAdminLogService";
import { LiveService } from "@/services/LiveService";
import { useAuth } from "@/context/AuthContext";
import { isProtectedSpot } from "@/constants/spots";
import axiosInstance from "@/api/axiosInstance";

interface SpotManagerModalProps {
  spot: MapSpot;
  onClose: () => void;
  onSpotUpdated?: () => void;
}

// Create custom marker icon for the draggable marker
const createDraggableMarkerIcon = () => {
  return L.divIcon({
    className: "draggable-spot-marker",
    html: `
      <div style="
        width: 32px;
        height: 32px;
        position: relative;
      ">
        <svg viewBox="0 0 24 32" width="32" height="40" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#3b82f6"/>
          <circle cx="12" cy="11" r="5" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
  });
};

// Create marker icon for original position
const createOriginalMarkerIcon = () => {
  return L.divIcon({
    className: "original-spot-marker",
    html: `
      <div style="
        width: 24px;
        height: 24px;
        position: relative;
      ">
        <svg viewBox="0 0 24 32" width="24" height="30" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3)); opacity: 0.6;">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#6b7280"/>
          <circle cx="12" cy="11" r="4" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [24, 30],
    iconAnchor: [12, 30],
  });
};

// Map click handler component
const MapClickHandler: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to center map on location and fix size
const MapCenterer: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
    // Invalidate size after a short delay to fix rendering in portals
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [center, zoom, map]);
  return null;
};

// Create nearby spot marker icon for location picker
const createNearbySpotIcon = (name: string) => {
  const displayName = name.length > 12 ? name.substring(0, 11) + "…" : name;
  return L.divIcon({
    className: "nearby-spot-marker",
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <svg viewBox="0 0 24 32" width="20" height="26" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#ef4444"/>
          <circle cx="12" cy="11" r="4" fill="white"/>
        </svg>
        <div style="background: rgba(0,0,0,0.7); color: white; padding: 1px 4px; border-radius: 2px; font-size: 9px; white-space: nowrap; margin-top: -2px;">${displayName}</div>
      </div>
    `,
    iconSize: [80, 36],
    iconAnchor: [40, 26],
  });
};

// Location Picker Modal
const LocationPickerModal: React.FC<{
  originalLat: number;
  originalLng: number;
  currentLat: number;
  currentLng: number;
  onConfirm: (lat: number, lng: number) => void;
  onCancel: () => void;
}> = ({ originalLat, originalLng, currentLat, currentLng, onConfirm, onCancel }) => {
  const [newLat, setNewLat] = useState(currentLat);
  const [newLng, setNewLng] = useState(currentLng);
  const [nearbySpots, setNearbySpots] = useState<NearbySpot[]>([]);
  const distance = calculateDistance(originalLat, originalLng, newLat, newLng);

  // Load nearby spots for reference
  useEffect(() => {
    const loadNearby = async () => {
      const spots = await SpotManagementService.getNearbySpots(originalLat, originalLng, 0, 5);
      setNearbySpots(spots);
    };
    loadNearby();
  }, [originalLat, originalLng]);

  const handleLocationSelect = (lat: number, lng: number) => {
    setNewLat(lat);
    setNewLng(lng);
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        backgroundColor: "rgba(0,0,0,0.8)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          backgroundColor: "#1f2937",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "16px", borderBottom: "1px solid #374151", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ color: "white", fontWeight: 600, fontSize: "18px", margin: 0 }}>Pick New Location</h3>
            <p style={{ color: "#9ca3af", fontSize: "14px", margin: "4px 0 0 0" }}>Click on the map or drag the marker to set the new position</p>
          </div>
          <button
            onClick={onCancel}
            style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#374151", color: "white", cursor: "pointer" }}
          >
            <svg style={{ width: "20px", height: "20px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Map */}
        <div style={{ height: "450px", width: "100%" }}>
          <MapContainer
            center={[currentLat, currentLng]}
            zoom={14}
            style={{ height: "100%", width: "100%", zIndex: 1 }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onLocationSelect={handleLocationSelect} />
            <MapCenterer center={[currentLat, currentLng]} zoom={14} />

            {/* Original position marker (gray) */}
            <Marker
              position={[originalLat, originalLng]}
              icon={createOriginalMarkerIcon()}
            />

            {/* Original position circle */}
            <Circle
              center={[originalLat, originalLng]}
              radius={100}
              pathOptions={{ color: "#6b7280", fillColor: "#6b7280", fillOpacity: 0.1, dashArray: "5,5" }}
            />

            {/* New position marker (blue, draggable) */}
            <Marker
              position={[newLat, newLng]}
              icon={createDraggableMarkerIcon()}
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target;
                  const position = marker.getLatLng();
                  setNewLat(position.lat);
                  setNewLng(position.lng);
                },
              }}
            />

            {/* Nearby spots for reference */}
            {nearbySpots.map((spot) => (
              <Marker
                key={`nearby-${spot.id}`}
                position={[spot.latitude, spot.longitude]}
                icon={createNearbySpotIcon(spot.name)}
                zIndexOffset={-500}
              />
            ))}
          </MapContainer>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px", borderTop: "1px solid #374151", backgroundColor: "#111827" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div>
              <span style={{ color: "#9ca3af", fontSize: "13px" }}>New coordinates: </span>
              <span style={{ color: "white", fontFamily: "monospace", fontSize: "14px" }}>
                {newLat.toFixed(6)}, {newLng.toFixed(6)}
              </span>
            </div>
            <div style={{ backgroundColor: distance > 1 ? "#fef3c7" : "#d1fae5", padding: "4px 12px", borderRadius: "12px" }}>
              <span style={{ color: distance > 1 ? "#92400e" : "#065f46", fontSize: "13px", fontWeight: 500 }}>
                {distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(2)}km`} from original
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={onCancel}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #4b5563",
                backgroundColor: "transparent",
                color: "#d1d5db",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log("Confirming location:", newLat, newLng);
                onConfirm(newLat, newLng);
              }}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#3b82f6",
                color: "white",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Wind Config Display — shows resolved wind provider for a spot
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  spot_config: { label: "Spot Config", color: "#34d399", bg: "#064e3b" },
  country_config: { label: "Country Config", color: "#60a5fa", bg: "#1e3a5f" },
  legacy_spot_fields: { label: "Legacy Windguru", color: "#fbbf24", bg: "#78350f" },
  global_default: { label: "Global Default", color: "#a78bfa", bg: "#4c1d95" },
  spot_not_found: { label: "Spot Not Found", color: "#f87171", bg: "#7f1d1d" },
  none: { label: "No Config", color: "#9ca3af", bg: "#374151" },
};

function WindConfigDisplay({ spotId }: { spotId: number }) {
  const [resolved, setResolved] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(`/api/admin/wind/config/spot/${spotId}/resolved`, { skipLoading: true })
      .then((resp) => {
        if (!cancelled) setResolved(resp.data);
      })
      .catch(() => {
        if (!cancelled) setResolved(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [spotId]);

  if (loading) {
    return (
      <div style={{ padding: "12px", color: "#6b7280", fontSize: "12px" }}>
        Loading wind config...
      </div>
    );
  }

  if (!resolved) return null;

  const source = resolved.source || "none";
  const sourceStyle = SOURCE_LABELS[source] || SOURCE_LABELS.none;
  const providers = resolved.providers || [];

  return (
    <div style={{ marginTop: "4px" }}>
      <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
        Active Wind Configuration
      </label>
      <div style={{
        backgroundColor: "#111827",
        borderRadius: "8px",
        border: "1px solid #374151",
        padding: "12px",
      }}>
        {/* Source badge */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: providers.length > 0 ? "10px" : "0" }}>
          <span style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 600,
            color: sourceStyle.color,
            backgroundColor: sourceStyle.bg,
          }}>
            {sourceStyle.label}
          </span>
          {source === "country_config" && resolved.countryName && (
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>
              via {resolved.countryName}
            </span>
          )}
          {source === "global_default" && (
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>
              Open-Meteo auto-select by location
            </span>
          )}
          {source === "legacy_spot_fields" && (
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>
              {resolved.wgLiveId ? `Live: ${resolved.wgLiveId}` : ""}
              {resolved.wgLiveId && resolved.wgSpotId ? " · " : ""}
              {resolved.wgSpotId ? `Forecast: ${resolved.wgSpotId}` : ""}
            </span>
          )}
        </div>

        {/* Provider list */}
        {providers.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {providers.map((p: any, i: number) => (
              <div
                key={p.id || i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <span style={{ color: "#6b7280", fontFamily: "monospace", width: "24px" }}>
                  P{p.priority}
                </span>
                <span style={{
                  padding: "1px 6px",
                  borderRadius: "3px",
                  fontSize: "11px",
                  fontWeight: 600,
                  backgroundColor: p.provider === "openmeteo" ? "#064e3b" : "#1e3a5f",
                  color: p.provider === "openmeteo" ? "#34d399" : "#60a5fa",
                }}>
                  {p.provider}
                </span>
                <span style={{
                  padding: "1px 6px",
                  borderRadius: "3px",
                  fontSize: "11px",
                  backgroundColor: p.dataType === "live" ? "#78350f" : "#312e81",
                  color: p.dataType === "live" ? "#fbbf24" : "#a5b4fc",
                }}>
                  {p.dataType}
                </span>
                <span style={{ color: "#d1d5db", fontFamily: "monospace" }}>
                  {p.modelId || p.stationId || "auto"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SpotManagerModal: React.FC<SpotManagerModalProps> = ({ spot, onClose, onSpotUpdated }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"edit" | "merge" | "stats" | "admin">("edit");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Edit state
  const [spotName, setSpotName] = useState(spot.name);
  const [spotLat, setSpotLat] = useState(spot.latitude);
  const [spotLng, setSpotLng] = useState(spot.longitude);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [wgSpotId, setWgSpotId] = useState<string>("");
  // Live-wind station assignment (SRF-2855): a Worker station id, validated before save.
  const [liveStationId, setLiveStationId] = useState<string>("");
  // The assignment as persisted — updated after a successful save so a later "clear" is a
  // change, not a no-op against the value the modal opened with.
  const [savedLiveStationId, setSavedLiveStationId] = useState<string>("");
  const [liveStationValid, setLiveStationValid] = useState<boolean | null>(true);
  const [liveStationReason, setLiveStationReason] = useState<string | undefined>(undefined);
  const [liveStationValidatedId, setLiveStationValidatedId] = useState<string>("");
  const onLiveStationValidity = useCallback((valid: boolean | null, reason?: string, validatedId?: string) => {
    setLiveStationValid(valid);
    setLiveStationReason(reason);
    setLiveStationValidatedId(validatedId ?? "");
  }, []);
  const liveStationChanged = liveStationId.trim() !== savedLiveStationId;
  // Only a CHANGED assignment must be validated: an unchanged one that the Worker cannot
  // confirm right now (Worker down, legacy id) must never block editing the other fields.
  const liveStationBlocksSave =
    liveStationChanged &&
    (liveStationValid !== true || liveStationValidatedId !== liveStationId.trim());
  const [selectedCountry, setSelectedCountry] = useState<{ id: number; name: string } | null>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [countrySuggestions, setCountrySuggestions] = useState<Array<{ id: number; name: string }>>([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Merge state
  const [nearbySpots, setNearbySpots] = useState<NearbySpot[]>([]);
  const [selectedMergeSpot, setSelectedMergeSpot] = useState<NearbySpot | null>(null);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [searchRadius, setSearchRadius] = useState<number>(10);
  const [spotIdSearch, setSpotIdSearch] = useState<string>("");
  const [searchingById, setSearchingById] = useState(false);
  const [isSwapped, setIsSwapped] = useState(false); // false = current spot is main, true = selected spot is main
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);

  // Stats state
  const [spotInfo, setSpotInfo] = useState<SpotInfo | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Live stats state (calculated from live sessions)
  const [liveSessions, setLiveSessions] = useState<Live[]>([]);
  const [loadingLiveStats, setLoadingLiveStats] = useState(false);
  const [liveStats, setLiveStats] = useState<{
    highestLiveJump: number;
    avgTop10JumpHeight: number;
  } | null>(null);

  // Admin log state
  const [adminLogs, setAdminLogs] = useState<SpotAdminLog[]>([]);
  const [loadingAdminLogs, setLoadingAdminLogs] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [undoingAction, setUndoingAction] = useState<number | null>(null);

  // Initialize windguru fields and country when spotInfo loads
  useEffect(() => {
    if (spotInfo) {
      const wgId = spotInfo.wgspotid ?? spot.wgspotid;
      setWgSpotId(wgId ? String(wgId) : "");
      const persisted = spotInfo.liveWind?.stationId ?? spotInfo.windStationId ?? "";
      setLiveStationId(persisted);
      setSavedLiveStationId(persisted);
      if (spotInfo.countryid && spotInfo.countryName) {
        setSelectedCountry({ id: spotInfo.countryid, name: spotInfo.countryName });
      }
    }
  }, [spotInfo, spot.wgspotid]);

  // Check for changes
  useEffect(() => {
    const nameOrCoordsChanged = spotName !== spot.name || spotLat !== spot.latitude || spotLng !== spot.longitude;

    const origWgSpotId = spotInfo?.wgspotid ?? spot.wgspotid;
    const wgSpotIdChanged = wgSpotId !== (origWgSpotId ? String(origWgSpotId) : "");

    const origCountryId = spotInfo?.countryid;
    const countryChanged = selectedCountry != null && origCountryId != null && selectedCountry.id !== origCountryId;

    setHasChanges(nameOrCoordsChanged || wgSpotIdChanged || liveStationChanged || countryChanged);
  }, [spotName, spotLat, spotLng, wgSpotId, liveStationChanged, selectedCountry, spot, spotInfo]);

  // Country search debounce
  useEffect(() => {
    if (countrySearch.length < 2) {
      setCountrySuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      const results = await SpotManagementService.searchCountries(countrySearch);
      setCountrySuggestions(results);
      setShowCountryDropdown(results.length > 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [countrySearch]);

  // Load nearby spots
  const loadNearbySpots = useCallback(async (radius: number = searchRadius) => {
    setLoadingNearby(true);
    const nearby = await SpotManagementService.getNearbySpots(spot.latitude, spot.longitude, spot.id, radius);
    setNearbySpots(nearby);
    setLoadingNearby(false);
  }, [spot, searchRadius]);

  // Search spot by ID
  const searchSpotById = useCallback(async () => {
    const id = parseInt(spotIdSearch.trim());
    if (isNaN(id) || id <= 0 || id === spot.id) {
      setMessage({ type: "error", text: id === spot.id ? "Cannot merge spot with itself" : "Please enter a valid spot ID" });
      return;
    }
    if (!user?.accesstoken) return;

    setSearchingById(true);
    const foundSpot = await SpotManagementService.getSpotInfo(id, user.accesstoken);
    setSearchingById(false);

    if (foundSpot) {
      const distance = calculateDistance(spot.latitude, spot.longitude, foundSpot.latitude, foundSpot.longitude);
      setSelectedMergeSpot({
        id: foundSpot.id,
        name: foundSpot.name,
        latitude: foundSpot.latitude,
        longitude: foundSpot.longitude,
        distance,
        sessionCount: foundSpot.totalsessions || 0,
        reviewCount: foundSpot.nreviews || 0,
      });
      setSpotIdSearch("");
      setMessage({ type: "success", text: `Found: ${foundSpot.name}` });
      setTimeout(() => setMessage(null), 2000);
    } else {
      setMessage({ type: "error", text: "Spot not found with this ID" });
    }
  }, [spotIdSearch, spot, user?.accesstoken]);

  // Load spot statistics
  const loadSpotStats = useCallback(async () => {
    if (!user?.accesstoken) return;
    setLoadingStats(true);
    const info = await SpotManagementService.getSpotInfo(spot.id, user.accesstoken);
    setSpotInfo(info);
    setLoadingStats(false);
  }, [spot.id, user?.accesstoken]);

  // Load live sessions and calculate stats
  const loadLiveStats = useCallback(async () => {
    if (!user?.accesstoken) return;
    setLoadingLiveStats(true);

    // Fetch live sessions for this spot
    const sessions = await LiveService.getLiveSessions(0, user.accesstoken, { spotId: spot.id });
    setLiveSessions(sessions);

    if (sessions.length > 0) {
      // Get all maxheight values from live sessions
      const maxHeights = sessions.map(s => s.maxheight).filter(h => h > 0);

      // Calculate highest live jump
      const highestLiveJump = maxHeights.length > 0 ? Math.max(...maxHeights) : 0;

      // Calculate avg top 10 max jump height
      const sortedHeights = [...maxHeights].sort((a, b) => b - a);
      const top10Heights = sortedHeights.slice(0, 10);
      const avgTop10JumpHeight = top10Heights.length > 0
        ? top10Heights.reduce((sum, h) => sum + h, 0) / top10Heights.length
        : 0;

      setLiveStats({ highestLiveJump, avgTop10JumpHeight });
    } else {
      setLiveStats({ highestLiveJump: 0, avgTop10JumpHeight: 0 });
    }

    setLoadingLiveStats(false);
  }, [spot.id, user?.accesstoken]);

  // Load admin logs
  const loadAdminLogs = useCallback(async () => {
    if (!user?.accesstoken) return;
    setLoadingAdminLogs(true);
    const logs = await SpotAdminLogService.getSpotLogs(spot.id, user.accesstoken);
    setAdminLogs(logs);
    setLoadingAdminLogs(false);
  }, [spot.id, user?.accesstoken]);

  // Handle adding a note
  const handleAddNote = async () => {
    if (!user?.accesstoken || !newNote.trim()) return;
    setAddingNote(true);
    const result = await SpotAdminLogService.addNote(spot.id, spot.name, newNote.trim(), user.accesstoken);
    if (result.success) {
      setNewNote("");
      loadAdminLogs(); // Refresh logs
      setMessage({ type: "success", text: "Note added successfully" });
      setTimeout(() => setMessage(null), 2000);
    } else {
      setMessage({ type: "error", text: result.message || "Failed to add note" });
    }
    setAddingNote(false);
  };

  // Handle undo action
  const handleUndoAction = async (logId: number, confirmWarnings: boolean = false): Promise<void> => {
    if (!user?.accesstoken) return;
    setUndoingAction(logId);
    const result = await SpotAdminLogService.undoAction(logId, user.accesstoken, confirmWarnings);
    if (result.success) {
      loadAdminLogs(); // Refresh logs
      onSpotUpdated?.(); // Refresh parent data
      setMessage({ type: "success", text: result.message || "Action undone successfully" });
    } else if (result.warnings && result.warnings.length > 0) {
      // Show warnings and ask for confirmation
      const warningText = result.warnings.join("\n");
      if (window.confirm(`Warning:\n${warningText}\n\nDo you want to proceed?`)) {
        setUndoingAction(null);
        await handleUndoAction(logId, true);
        return;
      } else {
        setMessage({ type: "error", text: "Undo cancelled" });
      }
    } else {
      setMessage({ type: "error", text: result.message || "Failed to undo action" });
    }
    setUndoingAction(null);
    setTimeout(() => setMessage(null), 3000);
  };

  // Load spotInfo on mount for wgspotid (needed for Spot Dashboard link)
  useEffect(() => {
    if (!spotInfo && user?.accesstoken) {
      loadSpotStats();
    }
  }, [user?.accesstoken]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "merge" && nearbySpots.length === 0) {
      loadNearbySpots(searchRadius);
    } else if (activeTab === "stats") {
      if (!liveStats) {
        loadLiveStats();
      }
    } else if (activeTab === "admin") {
      loadAdminLogs();
    }
  }, [activeTab, loadNearbySpots, loadLiveStats, loadAdminLogs, nearbySpots.length, liveStats, searchRadius]);

  // Handle radius change
  const handleRadiusChange = (newRadius: number) => {
    setSearchRadius(newRadius);
    setSelectedMergeSpot(null);
    loadNearbySpots(newRadius);
  };

  // Handle save
  const handleSave = async () => {
    if (!user?.accesstoken || !hasChanges) return;

    setIsLoading(true);
    setMessage(null);

    const parsedWgSpotId = wgSpotId.trim() ? parseInt(wgSpotId.trim()) : undefined;
    // Never save an unvalidated station id: only a CHANGED id is gated, and the verdict must
    // be about exactly the id being sent.
    if (liveStationBlocksSave) {
      setMessage({
        type: "error",
        text:
          liveStationValid === null
            ? "Still checking the live wind station id — try again in a moment"
            : liveStationReason || "Live wind station id is not valid",
      });
      return;
    }
    const liveStationToSend: string | null | undefined = liveStationChanged
      ? liveStationId.trim() || null
      : undefined;

    // Send countryid if it was changed from the original
    const origCountryId = spotInfo?.countryid;
    const countryIdToSend = selectedCountry && origCountryId != null && selectedCountry.id !== origCountryId
      ? selectedCountry.id : undefined;

    const result = await SpotManagementService.editSpot(
      user.accesstoken,
      spot.id,
      spotName,
      spotLat,
      spotLng,
      isNaN(parsedWgSpotId as number) ? undefined : parsedWgSpotId,
      liveStationToSend,
      countryIdToSend,
    );

    setIsLoading(false);

    if (result.success) {
      setMessage({ type: "success", text: "Spot updated successfully!" });
      if (liveStationToSend !== undefined) setSavedLiveStationId(liveStationToSend ?? "");
      setHasChanges(false);
      onSpotUpdated?.();
    } else {
      setMessage({ type: "error", text: result.message || "Failed to update spot" });
    }
  };

  // Handle merge
  const handleMerge = async () => {
    if (!user?.accesstoken || !selectedMergeSpot) return;

    setIsLoading(true);
    setMessage(null);

    // Determine main (target) and source spots based on swap state
    const mainSpotId = isSwapped ? selectedMergeSpot.id : spot.id;
    const sourceSpotId = isSwapped ? spot.id : selectedMergeSpot.id;

    const result = await SpotManagementService.mergeSpots(
      user.accesstoken,
      mainSpotId,
      [sourceSpotId]
    );

    setIsLoading(false);
    setShowMergeConfirm(false);

    if (result.success) {
      setMessage({ type: "success", text: "Spots merged successfully!" });
      setSelectedMergeSpot(null);
      setIsSwapped(false);
      loadNearbySpots(searchRadius);
      onSpotUpdated?.();
      // If current spot was merged into the other, close the modal
      if (isSwapped) {
        setTimeout(() => onClose(), 1500);
      }
    } else {
      setMessage({ type: "error", text: result.message || "Failed to merge spots" });
    }
  };

  // Handle location picker confirm
  const handleLocationConfirm = (lat: number, lng: number) => {
    console.log("handleLocationConfirm called with:", lat, lng);
    setSpotLat(lat);
    setSpotLng(lng);
    setShowLocationPicker(false);
    console.log("State updated, spotLat/spotLng should now be:", lat, lng);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: "success", text: "Copied to clipboard!" });
    setTimeout(() => setMessage(null), 2000);
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        backgroundColor: "rgba(0,0,0,0.7)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#1f2937",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "20px", borderBottom: "1px solid #374151", background: "linear-gradient(135deg, #374151 0%, #1f2937 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: "48px", height: "48px", background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg style={{ width: "28px", height: "28px", color: "white" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h2 style={{ color: "white", fontWeight: 700, fontSize: "20px", margin: 0 }}>Spot Manager</h2>
                <p style={{ color: "#9ca3af", fontSize: "14px", margin: "4px 0 0 0" }}>{spot.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer" }}
            >
              <svg style={{ width: "20px", height: "20px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #374151", backgroundColor: "#111827" }}>
          {[
            { id: "edit", label: "Edit", icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" },
            { id: "merge", label: "Merge", icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" },
            { id: "stats", label: "Statistics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
            { id: "admin", label: "Admin", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                flex: 1,
                padding: "12px",
                border: "none",
                backgroundColor: "transparent",
                color: activeTab === tab.id ? "#3b82f6" : "#9ca3af",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                borderBottom: activeTab === tab.id ? "2px solid #3b82f6" : "2px solid transparent",
                fontWeight: activeTab === tab.id ? 600 : 400,
              }}
            >
              <svg style={{ width: "16px", height: "16px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {/* Message */}
          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "16px",
                backgroundColor: message.type === "success" ? "#065f46" : "#991b1b",
                color: "white",
                fontSize: "14px",
              }}
            >
              {message.text}
            </div>
          )}

          {/* Edit Tab */}
          {activeTab === "edit" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Protected Spot Warning */}
              {isProtectedSpot(spot.id) && (
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px",
                    backgroundColor: "#78350f",
                    border: "1px solid #fbbf24",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <svg style={{ width: "20px", height: "20px", color: "#fbbf24", flexShrink: 0, marginTop: "1px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <div style={{ color: "#fef3c7", fontWeight: 600, fontSize: "14px" }}>Protected Spot</div>
                    <div style={{ color: "#fde68a", fontSize: "13px", marginTop: "2px" }}>
                      This is a system spot and cannot be edited or merged. It is used internally for sessions without a valid spot assignment.
                    </div>
                  </div>
                </div>
              )}

              {/* Name */}
              <div>
                <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
                  Spot Name
                </label>
                <input
                  type="text"
                  value={spotName}
                  onChange={(e) => setSpotName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #374151",
                    backgroundColor: "#111827",
                    color: "white",
                    fontSize: "15px",
                  }}
                />
              </div>

              {/* Country */}
              <div style={{ position: "relative" }}>
                <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
                  Country
                </label>
                {selectedCountry && !showCountryDropdown ? (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #374151",
                    backgroundColor: "#111827",
                  }}>
                    <span style={{ color: "white", fontSize: "14px" }}>{selectedCountry.name}</span>
                    <button
                      onClick={() => {
                        setCountrySearch("");
                        setShowCountryDropdown(true);
                      }}
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: "#374151",
                        color: "#d1d5db",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    onFocus={() => { if (countrySuggestions.length > 0) setShowCountryDropdown(true); }}
                    placeholder="Search country..."
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #374151",
                      backgroundColor: "#111827",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                )}
                {showCountryDropdown && countrySuggestions.length > 0 && (
                  <div style={{
                    position: "absolute",
                    zIndex: 50,
                    width: "100%",
                    marginTop: "4px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "8px",
                    boxShadow: "0 10px 15px rgba(0,0,0,0.3)",
                  }}>
                    {countrySuggestions.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setSelectedCountry(c);
                          setCountrySearch("");
                          setShowCountryDropdown(false);
                          setCountrySuggestions([]);
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          textAlign: "left",
                          backgroundColor: "transparent",
                          border: "none",
                          borderBottom: "1px solid #374151",
                          color: "#d1d5db",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#374151")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
                {selectedCountry && spotInfo?.countryid && selectedCountry.id !== spotInfo.countryid && (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#f59e0b" }}>
                    Country changed — saving will update timezone and continent
                  </div>
                )}
              </div>

              {/* Location */}
              <div>
                <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
                  Location
                </label>
                <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      step="0.000001"
                      value={spotLat}
                      onChange={(e) => setSpotLat(parseFloat(e.target.value))}
                      placeholder="Latitude"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #374151",
                        backgroundColor: "#111827",
                        color: "white",
                        fontSize: "14px",
                        fontFamily: "monospace",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      step="0.000001"
                      value={spotLng}
                      onChange={(e) => setSpotLng(parseFloat(e.target.value))}
                      placeholder="Longitude"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #374151",
                        backgroundColor: "#111827",
                        color: "white",
                        fontSize: "14px",
                        fontFamily: "monospace",
                      }}
                    />
                  </div>
                </div>
                <button
                  onClick={() => setShowLocationPicker(true)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px dashed #4b5563",
                    backgroundColor: "transparent",
                    color: "#9ca3af",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <svg style={{ width: "18px", height: "18px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Pick Location on Map
                </button>
              </div>

              {/* Windguru Settings */}
              <div>
                <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
                  Windguru Settings
                </label>
                <div style={{ display: "flex", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", color: "#6b7280", fontSize: "11px", marginBottom: "4px" }}>
                      Forecast Station ID
                    </label>
                    <input
                      type="number"
                      value={wgSpotId}
                      onChange={(e) => setWgSpotId(e.target.value)}
                      placeholder="e.g. 12345"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #374151",
                        backgroundColor: "#111827",
                        color: "white",
                        fontSize: "14px",
                        fontFamily: "monospace",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Live wind station — read from the Worker by id; validated before save */}
              <LiveStationPicker
                spotLat={spotLat}
                spotLng={spotLng}
                value={liveStationId}
                originalValue={savedLiveStationId}
                onChange={setLiveStationId}
                onValidityChange={onLiveStationValidity}
              />

              {/* Resolved Wind Configuration */}
              <WindConfigDisplay spotId={spot.id} />

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={!hasChanges || isLoading || isProtectedSpot(spot.id) || liveStationBlocksSave}
                style={{
                  padding: "14px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: hasChanges && !isProtectedSpot(spot.id) ? "#3b82f6" : "#374151",
                  color: hasChanges && !isProtectedSpot(spot.id) ? "white" : "#6b7280",
                  cursor: hasChanges && !isProtectedSpot(spot.id) ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  fontSize: "15px",
                }}
              >
                {isProtectedSpot(spot.id) ? "Protected Spot" : isLoading ? "Saving..." : "Save Changes"}
              </button>

              {/* Actions */}
              <div style={{ borderTop: "1px solid #374151", paddingTop: "20px" }}>
                <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "12px", fontWeight: 500 }}>
                  Quick Actions
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <a
                    href={`https://discover.thesurfr.app/spots/${encodeURIComponent(spot.name.toLowerCase().replace(/\s+/g, "-"))}/${spot.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#374151",
                      color: "#d1d5db",
                      textDecoration: "none",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <svg style={{ width: "14px", height: "14px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View on Discover
                  </a>
                  <a
                    href={`https://spot.thesurfr.app/dashboard?spot=${encodeURIComponent(spot.name)}&spotid=${spot.id}${(spot.wgspotid || spotInfo?.wgspotid) ? `&wgspotid=${spot.wgspotid || spotInfo?.wgspotid}` : ''}&showSpotName=true&windUnit=knots`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#374151",
                      color: "#d1d5db",
                      textDecoration: "none",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <svg style={{ width: "14px", height: "14px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Spot Dashboard
                  </a>
                  <a
                    href={`https://www.google.com/maps?q=${spot.latitude},${spot.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#374151",
                      color: "#d1d5db",
                      textDecoration: "none",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <svg style={{ width: "14px", height: "14px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Open in Maps
                  </a>
                  <button
                    onClick={() => copyToClipboard(spot.id.toString())}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#374151",
                      color: "#d1d5db",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <svg style={{ width: "14px", height: "14px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy ID: {spot.id}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Merge Tab */}
          {activeTab === "merge" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Protected Spot Warning */}
              {isProtectedSpot(spot.id) && (
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px",
                    backgroundColor: "#78350f",
                    border: "1px solid #fbbf24",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <svg style={{ width: "20px", height: "20px", color: "#fbbf24", flexShrink: 0, marginTop: "1px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <div style={{ color: "#fef3c7", fontWeight: 600, fontSize: "14px" }}>Protected Spot</div>
                    <div style={{ color: "#fde68a", fontSize: "13px", marginTop: "2px" }}>
                      This spot cannot be merged into another spot. It is a protected system spot used for sessions without a valid spot assignment.
                    </div>
                  </div>
                </div>
              )}

              {/* Search Options */}
              <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                {/* Search by ID */}
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", color: "#9ca3af", fontSize: "12px", marginBottom: "6px", fontWeight: 500 }}>
                    Search by Spot ID
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      value={spotIdSearch}
                      onChange={(e) => setSpotIdSearch(e.target.value)}
                      placeholder="Enter spot ID..."
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #374151",
                        backgroundColor: "#1f2937",
                        color: "white",
                        fontSize: "14px",
                      }}
                      onKeyDown={(e) => e.key === "Enter" && searchSpotById()}
                    />
                    <button
                      onClick={searchSpotById}
                      disabled={searchingById || !spotIdSearch.trim()}
                      style={{
                        padding: "10px 16px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: spotIdSearch.trim() ? "#3b82f6" : "#374151",
                        color: spotIdSearch.trim() ? "white" : "#6b7280",
                        cursor: spotIdSearch.trim() ? "pointer" : "not-allowed",
                        fontWeight: 500,
                        fontSize: "14px",
                      }}
                    >
                      {searchingById ? "..." : "Find"}
                    </button>
                  </div>
                </div>

                {/* Radius selector */}
                <div>
                  <label style={{ display: "block", color: "#9ca3af", fontSize: "12px", marginBottom: "6px", fontWeight: 500 }}>
                    Search Radius
                  </label>
                  <div style={{ display: "flex", gap: "6px" }}>
                    {[10, 25, 50, 100].map((radius) => (
                      <button
                        key={radius}
                        onClick={() => handleRadiusChange(radius)}
                        style={{
                          flex: 1,
                          padding: "8px",
                          borderRadius: "6px",
                          border: searchRadius === radius ? "2px solid #3b82f6" : "1px solid #374151",
                          backgroundColor: searchRadius === radius ? "#1e3a5f" : "#1f2937",
                          color: searchRadius === radius ? "#3b82f6" : "#9ca3af",
                          cursor: "pointer",
                          fontWeight: searchRadius === radius ? 600 : 400,
                          fontSize: "13px",
                        }}
                      >
                        {radius} km
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Selected spot for merge */}
              {selectedMergeSpot && (
                <div style={{ backgroundColor: "#1e3a5f", border: "2px solid #3b82f6", borderRadius: "8px", padding: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 500 }}>SELECTED FOR MERGE</span>
                    <button
                      onClick={() => { setSelectedMergeSpot(null); setIsSwapped(false); }}
                      style={{ padding: "4px 8px", borderRadius: "4px", border: "none", backgroundColor: "#374151", color: "#9ca3af", cursor: "pointer", fontSize: "11px" }}
                    >
                      Clear
                    </button>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: "16px", color: "white", marginBottom: "4px" }}>{selectedMergeSpot.name}</div>
                  <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                    ID: {selectedMergeSpot.id} • {selectedMergeSpot.distance.toFixed(1)} km away • {selectedMergeSpot.sessionCount} sessions • {selectedMergeSpot.reviewCount} reviews
                  </div>
                </div>
              )}

              {/* Merge Direction Display */}
              {selectedMergeSpot && (
                <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 500 }}>MERGE DIRECTION</span>
                    <button
                      onClick={() => setIsSwapped(!isSwapped)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #4b5563",
                        backgroundColor: "transparent",
                        color: "#9ca3af",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      <svg style={{ width: "14px", height: "14px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Swap
                    </button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* Source spot (will be deleted) */}
                    <div style={{ flex: 1, backgroundColor: "#7f1d1d", borderRadius: "8px", padding: "12px", border: "1px solid #dc2626" }}>
                      <div style={{ color: "#fca5a5", fontSize: "10px", fontWeight: 600, marginBottom: "4px" }}>SOURCE (DELETED)</div>
                      <div style={{ color: "white", fontWeight: 600, fontSize: "14px" }}>
                        {isSwapped ? spot.name : selectedMergeSpot.name}
                      </div>
                      <div style={{ color: "#fca5a5", fontSize: "11px", marginTop: "2px" }}>
                        ID: {isSwapped ? spot.id : selectedMergeSpot.id}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <svg style={{ width: "24px", height: "24px", color: "#6b7280" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                      <span style={{ color: "#6b7280", fontSize: "9px", marginTop: "2px" }}>merges into</span>
                    </div>

                    {/* Main spot (will keep) */}
                    <div style={{ flex: 1, backgroundColor: "#14532d", borderRadius: "8px", padding: "12px", border: "1px solid #22c55e" }}>
                      <div style={{ color: "#86efac", fontSize: "10px", fontWeight: 600, marginBottom: "4px" }}>MAIN (KEEPS)</div>
                      <div style={{ color: "white", fontWeight: 600, fontSize: "14px" }}>
                        {isSwapped ? selectedMergeSpot.name : spot.name}
                      </div>
                      <div style={{ color: "#86efac", fontSize: "11px", marginTop: "2px" }}>
                        ID: {isSwapped ? selectedMergeSpot.id : spot.id}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Nearby Spots List */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 500 }}>
                    NEARBY SPOTS ({nearbySpots.length})
                  </span>
                  {loadingNearby && (
                    <div style={{ width: "16px", height: "16px", border: "2px solid #374151", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                  )}
                </div>

                {!loadingNearby && nearbySpots.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "30px", color: "#6b7280", backgroundColor: "#111827", borderRadius: "8px" }}>
                    <svg style={{ width: "36px", height: "36px", margin: "0 auto 8px", opacity: 0.5 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <div style={{ fontSize: "13px" }}>No spots found within {searchRadius}km</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "200px", overflowY: "auto" }}>
                    {nearbySpots.filter(s => !isProtectedSpot(s.id)).map((nearbySpot) => (
                      <button
                        key={nearbySpot.id}
                        onClick={() => { setSelectedMergeSpot(nearbySpot); setIsSwapped(false); }}
                        style={{
                          padding: "12px",
                          borderRadius: "6px",
                          border: selectedMergeSpot?.id === nearbySpot.id ? "2px solid #3b82f6" : "1px solid #374151",
                          backgroundColor: selectedMergeSpot?.id === nearbySpot.id ? "#1e3a5f" : "#111827",
                          color: "white",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500, fontSize: "14px", marginBottom: "2px" }}>{nearbySpot.name}</div>
                          <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                            ID: {nearbySpot.id} • {nearbySpot.sessionCount} sessions • {nearbySpot.reviewCount} reviews
                          </div>
                        </div>
                        <span style={{ backgroundColor: "#374151", padding: "3px 8px", borderRadius: "10px", fontSize: "11px", color: "#d1d5db", whiteSpace: "nowrap" }}>
                          {nearbySpot.distance.toFixed(1)} km
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Merge Button */}
              <button
                onClick={() => setShowMergeConfirm(true)}
                disabled={!selectedMergeSpot || isProtectedSpot(spot.id)}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: selectedMergeSpot && !isProtectedSpot(spot.id) ? "#dc2626" : "#374151",
                  color: selectedMergeSpot && !isProtectedSpot(spot.id) ? "white" : "#6b7280",
                  cursor: selectedMergeSpot && !isProtectedSpot(spot.id) ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <svg style={{ width: "18px", height: "18px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                {isProtectedSpot(spot.id) ? "Protected Spot" : "Review & Merge"}
              </button>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === "stats" && (
            <div>
              {loadingStats ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                  <div style={{ width: "32px", height: "32px", border: "3px solid #374151", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }}></div>
                  Loading statistics...
                </div>
              ) : spotInfo ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {/* Basic Stats Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                    <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                      <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>Total Sessions</div>
                      <div style={{ color: "#3b82f6", fontSize: "24px", fontWeight: 700 }}>{spotInfo.totalsessions?.toLocaleString() || 0}</div>
                    </div>
                    <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                      <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>Total Riders</div>
                      <div style={{ color: "#8b5cf6", fontSize: "24px", fontWeight: 700 }}>{spotInfo.nriders?.toLocaleString() || 0}</div>
                    </div>
                    <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                      <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>Reviews</div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ color: "#fbbf24", fontSize: "24px", fontWeight: 700 }}>{spotInfo.meanreviews?.toFixed(1) || "N/A"}</span>
                        <span style={{ color: "#fbbf24" }}>★</span>
                        <span style={{ color: "#6b7280", fontSize: "14px" }}>({spotInfo.nreviews || 0})</span>
                      </div>
                    </div>
                    <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                      <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>Live Now</div>
                      <div style={{ color: "#22c55e", fontSize: "24px", fontWeight: 700 }}>{spotInfo.riderslive || 0}</div>
                    </div>
                  </div>

                  {/* Records */}
                  <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ color: "#9ca3af", fontSize: "13px", marginBottom: "12px", fontWeight: 500 }}>Spot Records</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>Max Height</div>
                        <div style={{ color: "#22c55e", fontSize: "18px", fontWeight: 600 }}>{spotInfo.maxheight?.toFixed(1) || "0"}m</div>
                      </div>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>Max Airtime</div>
                        <div style={{ color: "#3b82f6", fontSize: "18px", fontWeight: 600 }}>{spotInfo.maxairtime?.toFixed(1) || "0"}s</div>
                      </div>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>Max Distance</div>
                        <div style={{ color: "#8b5cf6", fontSize: "18px", fontWeight: 600 }}>{spotInfo.maxdistance?.toFixed(0) || "0"}m</div>
                      </div>
                    </div>
                  </div>

                  {/* Live Stats */}
                  <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ color: "#9ca3af", fontSize: "13px", marginBottom: "12px", fontWeight: 500 }}>
                      Live Stats
                      {loadingLiveStats && (
                        <span style={{ marginLeft: "8px", fontSize: "11px", color: "#6b7280" }}>(loading...)</span>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>Highest Live Jump</div>
                        <div style={{ color: "#f97316", fontSize: "18px", fontWeight: 600 }}>
                          {liveStats?.highestLiveJump?.toFixed(1) || "0"}m
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>Avg Top 10 Jump Height</div>
                        <div style={{ color: "#ec4899", fontSize: "18px", fontWeight: 600 }}>
                          {liveStats?.avgTop10JumpHeight?.toFixed(1) || "0"}m
                        </div>
                      </div>
                    </div>
                    {liveSessions.length > 0 && (
                      <div style={{ marginTop: "8px", color: "#6b7280", fontSize: "11px" }}>
                        Based on {liveSessions.length} live session{liveSessions.length !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>

                  {/* Location Info */}
                  <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "14px" }}>
                    <div style={{ color: "#9ca3af", fontSize: "13px", marginBottom: "12px", fontWeight: 500 }}>Location Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", fontSize: "13px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ color: "#6b7280" }}>Country: </span>
                        {(spotInfo.alpha2 || spot.alpha2) && (
                          <img
                            src={`https://flagcdn.com/w20/${(spotInfo.alpha2 || spot.alpha2)?.toLowerCase()}.png`}
                            alt=""
                            style={{ width: "16px", height: "auto", borderRadius: "2px" }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                          />
                        )}
                        <span style={{ color: "#d1d5db" }}>{spotInfo.countryName || spot.countryName || spotInfo.alpha2 || spot.alpha2 || "Unknown"}</span>
                      </div>
                      <div>
                        <span style={{ color: "#6b7280" }}>Continent: </span>
                        <span style={{ color: "#d1d5db" }}>{spotInfo.continentName || "Unknown"}</span>
                      </div>
                      <div>
                        <span style={{ color: "#6b7280" }}>Timezone: </span>
                        <span style={{ color: "#d1d5db" }}>
                          {spotInfo.timezoneName
                            ? spotInfo.timezoneName
                            : spotInfo.utcOffset !== undefined
                              ? `UTC${spotInfo.utcOffset >= 0 ? '+' : ''}${spotInfo.utcOffset}`
                              : "Unknown"}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#6b7280" }}>Coordinates: </span>
                        <span style={{ color: "#d1d5db", fontFamily: "monospace" }}>{spotInfo.latitude.toFixed(4)}, {spotInfo.longitude.toFixed(4)}</span>
                      </div>
                      <div>
                        <span style={{ color: "#6b7280" }}>Validated: </span>
                        <span style={{ color: spotInfo.validated ? "#22c55e" : "#ef4444" }}>{spotInfo.validated ? "Yes" : "No"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                  Failed to load statistics
                </div>
              )}
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === "admin" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Add Note Section */}
              <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "16px" }}>
                <div style={{ color: "#9ca3af", fontSize: "13px", marginBottom: "12px", fontWeight: 500 }}>Add Admin Note</div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note about this spot..."
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #374151",
                      backgroundColor: "#1f2937",
                      color: "white",
                      fontSize: "14px",
                      resize: "vertical",
                      minHeight: "60px",
                    }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    style={{
                      padding: "12px 20px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: addingNote || !newNote.trim() ? "#374151" : "#3b82f6",
                      color: "white",
                      cursor: addingNote || !newNote.trim() ? "not-allowed" : "pointer",
                      fontWeight: 500,
                      alignSelf: "flex-end",
                    }}
                  >
                    {addingNote ? "Adding..." : "Add Note"}
                  </button>
                </div>
              </div>

              {/* Activity Log */}
              <div style={{ backgroundColor: "#111827", borderRadius: "8px", padding: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <div style={{ color: "#9ca3af", fontSize: "13px", fontWeight: 500 }}>Activity Log</div>
                  <button
                    onClick={loadAdminLogs}
                    disabled={loadingAdminLogs}
                    style={{
                      padding: "4px 12px",
                      borderRadius: "4px",
                      border: "1px solid #374151",
                      backgroundColor: "transparent",
                      color: "#9ca3af",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    {loadingAdminLogs ? "Loading..." : "Refresh"}
                  </button>
                </div>

                {loadingAdminLogs ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                    <div style={{ width: "32px", height: "32px", border: "3px solid #374151", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }}></div>
                    Loading activity log...
                  </div>
                ) : adminLogs.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                    No activity recorded for this spot yet
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {adminLogs.map((log) => {
                      const actionColor = SpotAdminLogService.getActionTypeColor(log.actionType);
                      return (
                        <div
                          key={log.id}
                          style={{
                            backgroundColor: "#1f2937",
                            borderRadius: "8px",
                            padding: "14px",
                            border: log.isUndone ? "1px dashed #4b5563" : "1px solid #374151",
                            opacity: log.isUndone ? 0.6 : 1,
                          }}
                        >
                          {/* Header Row */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  backgroundColor: actionColor.bg,
                                  color: actionColor.text,
                                  fontSize: "11px",
                                  fontWeight: 600,
                                }}
                              >
                                {SpotAdminLogService.formatActionType(log.actionType)}
                              </span>
                              {log.isUndone && (
                                <span style={{ color: "#f59e0b", fontSize: "11px" }}>UNDONE</span>
                              )}
                            </div>
                            <span style={{ color: "#6b7280", fontSize: "11px" }}>
                              {SpotAdminLogService.formatRelativeTime(log.createdAt)}
                            </span>
                          </div>

                          {/* Content */}
                          <div style={{ color: "#d1d5db", fontSize: "13px", marginBottom: "8px" }}>
                            {log.actionType === ACTION_TYPES.MERGE && (
                              <>
                                {log.notes ? (
                                  <div style={{ whiteSpace: "pre-wrap" }}>{log.notes}</div>
                                ) : (
                                  <>
                                    Merged <strong>{log.sourceSpotName}</strong> (ID: {log.sourceSpotId}) into{" "}
                                    <strong>{log.targetSpotName}</strong> (ID: {log.targetSpotId})
                                    {(log.sessionsAffected || log.reviewsAffected) && (
                                      <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px" }}>
                                        {log.sessionsAffected} sessions, {log.reviewsAffected} reviews affected
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            {log.actionType === ACTION_TYPES.NOTE && log.notes}
                            {log.actionType === ACTION_TYPES.DECLINE && (
                              <span style={{ color: "#fca5a5" }}>{log.notes || "Declined spot request"}</span>
                            )}
                            {log.actionType === ACTION_TYPES.EDIT && (
                              <>{log.notes || "Edited spot details"}</>
                            )}
                            {log.actionType === ACTION_TYPES.RENAME && (
                              <>{log.notes || "Renamed spot"}</>
                            )}
                            {log.actionType === ACTION_TYPES.CREATE && (
                              <>{log.notes || "Created spot"}</>
                            )}
                            {(log.actionType === ACTION_TYPES.UNDO_MERGE ||
                              log.actionType === ACTION_TYPES.UNDO_RENAME ||
                              log.actionType === ACTION_TYPES.UNDO_EDIT) && (
                              <>{log.notes || `Reversed previous action (Log #${log.relatedLogId})`}</>
                            )}
                          </div>

                          {/* Admin Info */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ color: "#6b7280", fontSize: "11px" }}>
                              by {log.adminUsername || `Admin #${log.adminUserId}`}
                            </span>

                            {/* Undo Button */}
                            {log.canUndo && !log.isUndone && (
                              <button
                                onClick={() => handleUndoAction(log.id)}
                                disabled={undoingAction === log.id}
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "4px",
                                  border: "1px solid #f59e0b",
                                  backgroundColor: "transparent",
                                  color: "#f59e0b",
                                  cursor: undoingAction === log.id ? "not-allowed" : "pointer",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                }}
                              >
                                {undoingAction === log.id ? "Undoing..." : "Undo"}
                              </button>
                            )}
                            {log.undoBlockedReason && (
                              <span style={{ color: "#ef4444", fontSize: "11px" }}>
                                {log.undoBlockedReason}
                              </span>
                            )}
                          </div>

                          {/* Undo Warnings */}
                          {log.undoWarnings && log.undoWarnings.length > 0 && (
                            <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#78350f", borderRadius: "4px" }}>
                              <div style={{ color: "#fde68a", fontSize: "11px" }}>
                                {log.undoWarnings.map((warning, idx) => (
                                  <div key={idx}>⚠️ {warning}</div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Undo Info */}
                          {log.isUndone && log.undoneAt && (
                            <div style={{ marginTop: "8px", color: "#9ca3af", fontSize: "11px" }}>
                              Undone {SpotAdminLogService.formatRelativeTime(log.undoneAt)}
                              {log.undoneByUsername && ` by ${log.undoneByUsername}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPickerModal
          originalLat={spot.latitude}
          originalLng={spot.longitude}
          currentLat={spotLat}
          currentLng={spotLng}
          onConfirm={handleLocationConfirm}
          onCancel={() => setShowLocationPicker(false)}
        />
      )}

      {/* Merge Confirmation Dialog */}
      {showMergeConfirm && selectedMergeSpot && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            backgroundColor: "rgba(0,0,0,0.8)",
          }}
          onClick={() => setShowMergeConfirm(false)}
        >
          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "480px",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ backgroundColor: "#dc2626", padding: "20px", textAlign: "center" }}>
              <svg style={{ width: "48px", height: "48px", margin: "0 auto 8px", color: "white" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 style={{ color: "white", fontSize: "20px", fontWeight: 700, margin: 0 }}>Confirm Merge</h3>
              <p style={{ color: "#fecaca", fontSize: "13px", margin: "8px 0 0 0" }}>This action cannot be undone!</p>
            </div>

            {/* Content */}
            <div style={{ padding: "20px" }}>
              <div style={{ marginBottom: "16px" }}>
                <p style={{ color: "#9ca3af", fontSize: "14px", marginBottom: "16px", textAlign: "center" }}>
                  You are about to merge two spots. All sessions, reviews, and data will be transferred to the main spot.
                </p>
              </div>

              {/* Merge Details */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                {/* Source spot (will be deleted) */}
                <div style={{ flex: 1, backgroundColor: "#7f1d1d", borderRadius: "8px", padding: "14px", border: "1px solid #dc2626" }}>
                  <div style={{ color: "#fca5a5", fontSize: "10px", fontWeight: 600, marginBottom: "6px" }}>⛔ WILL BE DELETED</div>
                  <div style={{ color: "white", fontWeight: 600, fontSize: "15px", marginBottom: "4px" }}>
                    {isSwapped ? spot.name : selectedMergeSpot.name}
                  </div>
                  <div style={{ color: "#fca5a5", fontSize: "11px" }}>
                    ID: {isSwapped ? spot.id : selectedMergeSpot.id}
                  </div>
                  <div style={{ color: "#fca5a5", fontSize: "11px", marginTop: "4px" }}>
                    {isSwapped
                      ? `Sessions & reviews will be moved`
                      : `${selectedMergeSpot.sessionCount} sessions • ${selectedMergeSpot.reviewCount} reviews`}
                  </div>
                </div>

                {/* Arrow */}
                <svg style={{ width: "28px", height: "28px", color: "#6b7280", flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>

                {/* Main spot (will keep) */}
                <div style={{ flex: 1, backgroundColor: "#14532d", borderRadius: "8px", padding: "14px", border: "1px solid #22c55e" }}>
                  <div style={{ color: "#86efac", fontSize: "10px", fontWeight: 600, marginBottom: "6px" }}>✓ MAIN SPOT (KEEPS)</div>
                  <div style={{ color: "white", fontWeight: 600, fontSize: "15px", marginBottom: "4px" }}>
                    {isSwapped ? selectedMergeSpot.name : spot.name}
                  </div>
                  <div style={{ color: "#86efac", fontSize: "11px" }}>
                    ID: {isSwapped ? selectedMergeSpot.id : spot.id}
                  </div>
                  <div style={{ color: "#86efac", fontSize: "11px", marginTop: "4px" }}>
                    Will receive all data
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div style={{ backgroundColor: "#78350f", borderRadius: "8px", padding: "12px", marginBottom: "20px", border: "1px solid #fbbf24" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <svg style={{ width: "20px", height: "20px", color: "#fbbf24", flexShrink: 0, marginTop: "1px" }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div style={{ color: "#fef3c7", fontSize: "13px" }}>
                    <strong>Warning:</strong> The source spot will be permanently deleted. All sessions and reviews will be transferred to the main spot.
                    {isSwapped && <span style={{ display: "block", marginTop: "6px", color: "#fbbf24" }}>Note: The current spot you opened will be deleted. You will be redirected after merge.</span>}
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setShowMergeConfirm(false)}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: "8px",
                    border: "1px solid #4b5563",
                    backgroundColor: "transparent",
                    color: "#d1d5db",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "15px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMerge}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#dc2626",
                    color: "white",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "15px",
                    opacity: isLoading ? 0.7 : 1,
                  }}
                >
                  {isLoading ? "Merging..." : "Confirm Merge"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SpotManagerModal;
